import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { transactionsAPI, importAPI, categoriesAPI, accountsAPI } from '../services/api';

// Debounce hook for search input
const useDebounce = (value: string, delay: number): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

interface Transaction {
  id: number;
  date: string;
  description: string;
  amount: number;
  transaction_type?: string;  // 'debit' or 'credit'
  category?: { id: number; name: string; budget_priority?: string; color?: string };
  account?: { id: number; name: string };
  is_reviewed: boolean;
  tags?: { id: number; name: string; color: string }[];
  is_duplicate?: boolean;
  duplicate_of_id?: number;
  deleted_at?: string;
  deleted_by?: string;
  ai_categorized?: boolean;
  ai_confidence?: number;
  needs_review?: boolean;
  is_transfer?: boolean;
  batch_id?: number;
}

interface Category {
  id: number;
  name: string;
  budget_priority?: string;
  color?: string;
}

interface Account {
  id: number;
  name: string;
  type: string;
}

interface ImportPreview {
  headers: string[];
  preview_rows: string[][];
  total_rows: number;
  mapping: {
    date_column?: string;
    description_column?: string;
    amount_column?: string;
    debit_column?: string;
    credit_column?: string;
    balance_column?: string;
  };
  date_format?: string;
  confidence: number;
  unmapped_columns: string[];
  file_type: string;
  filename_info: {
    account_id?: string;
    account_type?: string;
    emoji?: string;
    suggested_name?: string;
    date?: string;
  };
}

interface ImportBatch {
  id: number;
  filename: string;
  file_type: string;
  total_rows: number;
  imported_count: number;
  duplicate_count: number;
  status: string;
  created_at: string;
  account?: { id: number; name: string };
}

type TabType = 'all' | 'duplicates' | 'deleted' | 'batches' | 'accounts';

interface AccountWithStats {
  id: number;
  name: string;
  account_type: string;
  institution?: string;
  balance: number;
  currency: string;
  is_active: boolean;
  transaction_count?: number;
  last_transaction_date?: string;
}

interface AccountsSummary {
  total_assets: number;
  total_liabilities: number;
  net_worth: number;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(value);
};

const Transactions: React.FC = () => {
  // Main state
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [deletedTransactions, setDeletedTransactions] = useState<Transaction[]>([]);
  const [duplicateTransactions, setDuplicateTransactions] = useState<Transaction[]>([]);
  const [importBatches, setImportBatches] = useState<ImportBatch[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('all');

  // Pagination & search & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);  // Debounce search by 300ms
  const [filterCategory, setFilterCategory] = useState<number | null>(null);
  const [filterAccount, setFilterAccount] = useState<number | null>(null);
  const [filterUncategorized, setFilterUncategorized] = useState(false);

  // Sorting
  const [sortColumn, setSortColumn] = useState<'date' | 'description' | 'account' | 'category' | 'amount'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Bulk category modal
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState<number | null>(null);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryPriority, setNewCategoryPriority] = useState<string>('');

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [dateFormat, setDateFormat] = useState('%Y-%m-%d');
  const [selectedAccount, setSelectedAccount] = useState<number | null>(null);
  const [useAI, setUseAI] = useState(true);
  const [flagDuplicates, setFlagDuplicates] = useState(true);
  const [importMode, setImportMode] = useState<'preview' | 'quick'>('preview');
  const [importStep, setImportStep] = useState<'upload' | 'mapping' | 'preview' | 'result'>('upload');
  const [importResult, setImportResult] = useState<any>(null);
  const [importing, setImporting] = useState(false);

  // Selection state
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(new Set());

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'selected' | 'date_range' | 'account'>('selected');
  const [deleteDateStart, setDeleteDateStart] = useState('');
  const [deleteDateEnd, setDeleteDateEnd] = useState('');
  const [deleteAccountId, setDeleteAccountId] = useState<number | null>(null);

  // Accounts tab state
  const [accountsWithStats, setAccountsWithStats] = useState<AccountWithStats[]>([]);
  const [accountsSummary, setAccountsSummary] = useState<AccountsSummary>({ total_assets: 0, total_liabilities: 0, net_worth: 0 });
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountWithStats | null>(null);
  const [accountFormData, setAccountFormData] = useState({
    name: '',
    account_type: 'checking',
    institution: '',
    balance: '',
    currency: 'CAD',
  });

  // Balance history state
  const [showBalanceHistoryModal, setShowBalanceHistoryModal] = useState(false);
  const [balanceHistoryAccount, setBalanceHistoryAccount] = useState<AccountWithStats | null>(null);
  const [balanceHistory, setBalanceHistory] = useState<{date: string; balance: number; source: string; id?: number; notes?: string}[]>([]);
  const [showAddSnapshotForm, setShowAddSnapshotForm] = useState(false);
  const [snapshotFormData, setSnapshotFormData] = useState({ date: '', balance: '', notes: '' });

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    try {
      const response = await transactionsAPI.getAll({
        page,
        per_page: 100,  // Show more transactions
        search: debouncedSearchTerm || undefined,
        category_id: filterCategory || undefined,
        account_id: filterAccount || undefined,
      });
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearchTerm, filterCategory, filterAccount]);

  const fetchDeleted = useCallback(async () => {
    try {
      const response = await transactionsAPI.getDeleted();
      setDeletedTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch deleted:', error);
      setDeletedTransactions([]);
    }
  }, []);

  const fetchDuplicates = useCallback(async () => {
    try {
      const response = await transactionsAPI.getDuplicates();
      setDuplicateTransactions(response.data.duplicates || []);
    } catch (error) {
      console.error('Failed to fetch duplicates:', error);
      setDuplicateTransactions([]);
    }
  }, []);

  const fetchBatches = useCallback(async () => {
    try {
      const response = await importAPI.getBatches();
      setImportBatches(response.data.batches || []);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
      setImportBatches([]);
    }
  }, []);

  const fetchAccountsWithStats = useCallback(async () => {
    try {
      const response = await accountsAPI.getAll();
      const accountsData = response.data.accounts || response.data || [];
      setAccounts(accountsData);  // Also set accounts for dropdowns
      setAccountsWithStats(accountsData);
      setAccountsSummary(response.data.summary || { total_assets: 0, total_liabilities: 0, net_worth: 0 });
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      setAccountsWithStats([]);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        // Sort categories alphabetically
        const sorted = [...response.data].sort((a: Category, b: Category) =>
          a.name.localeCompare(b.name)
        );
        setCategories(sorted);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
    fetchAccountsWithStats();  // Single call for both accounts dropdown and stats
  }, [fetchAccountsWithStats]);

  // Fetch tab-specific data
  useEffect(() => {
    if (activeTab === 'deleted') fetchDeleted();
    if (activeTab === 'duplicates') fetchDuplicates();
    if (activeTab === 'batches') fetchBatches();
    if (activeTab === 'accounts') fetchAccountsWithStats();
  }, [activeTab, fetchDeleted, fetchDuplicates, fetchBatches, fetchAccountsWithStats]);

  // File handling
  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    setImportFiles(fileArray);
    setCurrentFileIndex(0);
    setImportResult(null);

    // Preview first file - only switch to mapping step after preview succeeds
    try {
      const response = await importAPI.previewEnhanced(fileArray[0]);
      const preview = response.data;
      setImportPreview(preview);
      setColumnMapping(preview.mapping || {});
      setDateFormat(preview.date_format || '%Y-%m-%d');

      // If filename has account info, try to match existing account
      if (preview.filename_info?.account_id) {
        const matchingAccount = accounts.find(
          (a) => a.name.includes(preview.filename_info.account_id || '')
        );
        if (matchingAccount) {
          setSelectedAccount(matchingAccount.id);
        }
      }
      setImportStep('mapping');
    } catch (error: any) {
      console.error('Failed to preview file:', error);
      alert('Failed to preview file: ' + (error.response?.data?.error || error.message));
      setImportFiles([]);
    }
  };

  const previewFile = async (file: File) => {
    try {
      const response = await importAPI.previewEnhanced(file);
      const preview = response.data;
      setImportPreview(preview);

      // Set detected mapping
      setColumnMapping(preview.mapping || {});
      setDateFormat(preview.date_format || '%Y-%m-%d');

      // If filename has account info, try to match existing account
      if (preview.filename_info?.account_id) {
        const matchingAccount = accounts.find(
          (a) => a.name.includes(preview.filename_info.account_id || '')
        );
        if (matchingAccount) {
          setSelectedAccount(matchingAccount.id);
        }
      }
    } catch (error) {
      console.error('Failed to preview file:', error);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Import processing
  const handleImport = async () => {
    if (importFiles.length === 0) return;

    setImporting(true);
    const results: any[] = [];

    for (let i = 0; i < importFiles.length; i++) {
      setCurrentFileIndex(i);
      const file = importFiles[i];

      try {
        // Preview if not already done
        if (i > 0) {
          await previewFile(file);
        }

        const response = await importAPI.processEnhanced(file, {
          mapping: columnMapping,
          date_format: dateFormat,
          account_id: selectedAccount || undefined,
          use_ai: useAI,
          flag_duplicates: flagDuplicates,
        });

        results.push({
          filename: file.name,
          ...response.data,
        });
      } catch (error: any) {
        results.push({
          filename: file.name,
          error: error.response?.data?.error || 'Import failed',
        });
      }
    }

    setImportResult(results);
    setImportStep('result');
    setImporting(false);
    fetchTransactions();
    fetchDuplicates();
    fetchBatches();
  };

  // Category change
  const handleCategoryChange = async (transactionId: number, categoryId: number) => {
    try {
      await transactionsAPI.update(transactionId, {
        category_id: categoryId,
        is_reviewed: true,
      });
      fetchTransactions();
    } catch (error) {
      console.error('Failed to update transaction:', error);
    }
  };

  // Soft delete handlers
  const handleSoftDelete = async () => {
    try {
      const options: any = {};

      if (deleteMode === 'selected') {
        options.transaction_ids = Array.from(selectedTransactions);
      } else if (deleteMode === 'date_range') {
        options.start_date = deleteDateStart;
        options.end_date = deleteDateEnd;
      } else if (deleteMode === 'account') {
        options.account_id = deleteAccountId;
      }

      await transactionsAPI.softDelete(options);
      setShowDeleteModal(false);
      setSelectedTransactions(new Set());
      fetchTransactions();
      fetchDeleted();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const handleRestore = async (ids: number[]) => {
    try {
      await transactionsAPI.restore(ids);
      fetchTransactions();
      fetchDeleted();
    } catch (error) {
      console.error('Failed to restore:', error);
    }
  };

  // Duplicate handlers
  const handleDeleteDuplicate = async (id: number) => {
    try {
      await transactionsAPI.deleteDuplicate(id);
      fetchDuplicates();
      fetchTransactions();
    } catch (error) {
      console.error('Failed to delete duplicate:', error);
    }
  };

  const handleDeleteAllDuplicates = async () => {
    if (!window.confirm('Are you sure you want to delete ALL duplicates?')) return;
    try {
      await transactionsAPI.deleteAllDuplicates();
      fetchDuplicates();
      fetchTransactions();
    } catch (error) {
      console.error('Failed to delete all duplicates:', error);
    }
  };

  const handleMarkNotDuplicate = async (id: number) => {
    try {
      await transactionsAPI.markNotDuplicate(id);
      fetchDuplicates();
      fetchTransactions();
    } catch (error) {
      console.error('Failed to mark as not duplicate:', error);
    }
  };

  // Batch handlers
  const handleDeleteBatch = async (batchId: number) => {
    if (!window.confirm('Delete all transactions from this import batch?')) return;
    try {
      await importAPI.deleteBatch(batchId);
      fetchBatches();
      fetchTransactions();
      fetchDeleted();
    } catch (error) {
      console.error('Failed to delete batch:', error);
    }
  };

  // AI Categorization
  const handleAICategorize = async () => {
    const ids = Array.from(selectedTransactions);
    if (ids.length === 0) return;

    try {
      await transactionsAPI.aiCategorize(ids);
      setSelectedTransactions(new Set());
      fetchTransactions();
    } catch (error) {
      console.error('Failed to categorize:', error);
    }
  };

  // Selection helpers
  const toggleSelection = (id: number) => {
    const newSet = new Set(selectedTransactions);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedTransactions(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedTransactions.size === sortedTransactions.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(sortedTransactions.map((t) => t.id)));
    }
  };

  // Sorting handler
  const handleSort = (column: 'date' | 'description' | 'account' | 'category' | 'amount') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sorted transactions
  const sortedTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Apply uncategorized filter
    if (filterUncategorized) {
      filtered = filtered.filter((t) => !t.category);
    }

    return filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortColumn) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'description':
          comparison = a.description.localeCompare(b.description);
          break;
        case 'account':
          comparison = (a.account?.name || '').localeCompare(b.account?.name || '');
          break;
        case 'category':
          comparison = (a.category?.name || 'zzz').localeCompare(b.category?.name || 'zzz');
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
      }
      // Use id as secondary sort for stable ordering when primary sort is equal
      if (comparison === 0) {
        comparison = a.id - b.id;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [transactions, sortColumn, sortDirection, filterUncategorized]);

  // Bulk category handler
  const handleBulkCategoryApply = async () => {
    if (!bulkCategoryId || selectedTransactions.size === 0) return;

    try {
      // Update each selected transaction
      const updatePromises = Array.from(selectedTransactions).map((id) =>
        transactionsAPI.update(id, {
          category_id: bulkCategoryId,
          is_reviewed: true,
        })
      );
      await Promise.all(updatePromises);
      setShowBulkCategoryModal(false);
      setSelectedTransactions(new Set());
      setBulkCategoryId(null);
      setShowNewCategoryForm(false);
      fetchTransactions();
    } catch (error) {
      console.error('Failed to bulk update categories:', error);
    }
  };

  // Quick-add new category handler
  const handleQuickAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const response = await categoriesAPI.create({
        name: newCategoryName.trim(),
        budget_priority: newCategoryPriority || null,
        color: '#6366f1', // Default color
      });
      // Refresh categories and auto-select the new one
      const catResponse = await categoriesAPI.getAll();
      const sorted = [...catResponse.data].sort((a: Category, b: Category) =>
        a.name.localeCompare(b.name)
      );
      setCategories(sorted);
      setBulkCategoryId(response.data.id);
      // Reset form
      setShowNewCategoryForm(false);
      setNewCategoryName('');
      setNewCategoryPriority('');
    } catch (error) {
      console.error('Failed to create category:', error);
    }
  };

  // Sort categories with Ignore at top
  const sortedCategoriesForDropdown = useMemo(() => {
    const ignoreCategory = categories.find((c) => c.name === 'Ignore');
    const otherCategories = categories.filter((c) => c.name !== 'Ignore');
    return ignoreCategory ? [ignoreCategory, ...otherCategories] : otherCategories;
  }, [categories]);

  // Close and reset import modal
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFiles([]);
    setImportPreview(null);
    setImportStep('upload');
    setImportResult(null);
    setColumnMapping({});
    setSelectedAccount(null);
  };

  // Unmapped columns for drag-drop
  const unmappedColumns = useMemo(() => {
    if (!importPreview) return [];
    const mapped = new Set(Object.values(columnMapping).filter(Boolean));
    return importPreview.headers.filter((h) => !mapped.has(h));
  }, [importPreview, columnMapping]);

  // Required fields
  const requiredFields = [
    { key: 'date_column', label: 'Date', required: true },
    { key: 'description_column', label: 'Description', required: true },
    { key: 'amount_column', label: 'Amount', required: false },
    { key: 'debit_column', label: 'Debit (Expenses)', required: false },
    { key: 'credit_column', label: 'Credit (Income)', required: false },
  ];

  const hasAmountMapping = columnMapping.amount_column || (columnMapping.debit_column && columnMapping.credit_column);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading transactions...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="page-title">Transactions</h1>
          <p className="page-subtitle">
            {activeTab === 'all' && `${sortedTransactions.length} transactions${filterUncategorized ? ' (uncategorized)' : ''}`}
            {activeTab === 'duplicates' && `${duplicateTransactions.length} duplicates`}
            {activeTab === 'deleted' && `${deletedTransactions.length} deleted`}
            {activeTab === 'batches' && `${importBatches.length} batches`}
            {activeTab === 'accounts' && `${accountsWithStats.length} accounts`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {selectedTransactions.size > 0 && activeTab === 'all' && (
            <>
              <button className="btn btn-primary" onClick={() => setShowBulkCategoryModal(true)}>
                Set Category ({selectedTransactions.size})
              </button>
              <button className="btn btn-secondary" onClick={handleAICategorize}>
                AI Categorize ({selectedTransactions.size})
              </button>
              <button className="btn btn-danger" onClick={() => { setDeleteMode('selected'); setShowDeleteModal(true); }}>
                Delete ({selectedTransactions.size})
              </button>
            </>
          )}
          <button className="btn btn-primary" onClick={() => setShowImportModal(true)}>
            Import Files
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
        {(['all', 'duplicates', 'deleted', 'batches', 'accounts'] as TabType[]).map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setSelectedTransactions(new Set()); }}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: activeTab === tab ? 'var(--bg-secondary)' : 'transparent',
              borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === tab ? 600 : 400,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {tab}
            {tab === 'duplicates' && duplicateTransactions.length > 0 && (
              <span style={{ marginLeft: '0.5rem', background: 'var(--warning)', color: '#000', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem' }}>
                {duplicateTransactions.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters (only for main transactions) */}
      {activeTab === 'all' && (
        <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              style={{ flex: '1', minWidth: '200px' }}
            />
            <select
              className="form-input"
              value={filterCategory || ''}
              onChange={(e) => { setFilterCategory(e.target.value ? parseInt(e.target.value) : null); setPage(1); }}
              style={{ width: '180px' }}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <select
              className="form-input"
              value={filterAccount || ''}
              onChange={(e) => { setFilterAccount(e.target.value ? parseInt(e.target.value) : null); setPage(1); }}
              style={{ width: '180px' }}
            >
              <option value="">All Accounts</option>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>{acc.name}</option>
              ))}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={filterUncategorized}
                onChange={(e) => setFilterUncategorized(e.target.checked)}
              />
              <span>Uncategorized only</span>
            </label>
            {(filterCategory || filterAccount || searchTerm || filterUncategorized) && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setFilterCategory(null); setFilterAccount(null); setSearchTerm(''); setFilterUncategorized(false); setPage(1); }}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Content based on active tab */}
      {activeTab === 'all' && (
        <TransactionTable
          transactions={sortedTransactions}
          categories={categories}
          selectedTransactions={selectedTransactions}
          onToggleSelection={toggleSelection}
          onToggleSelectAll={toggleSelectAll}
          onCategoryChange={handleCategoryChange}
          onDelete={(id) => { setSelectedTransactions(new Set([id])); setDeleteMode('selected'); setShowDeleteModal(true); }}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}

      {activeTab === 'duplicates' && (
        <DuplicatesView
          duplicates={duplicateTransactions}
          onDelete={handleDeleteDuplicate}
          onDeleteAll={handleDeleteAllDuplicates}
          onMarkNotDuplicate={handleMarkNotDuplicate}
        />
      )}

      {activeTab === 'deleted' && (
        <DeletedView
          transactions={deletedTransactions}
          onRestore={handleRestore}
          onRestoreAll={() => handleRestore(deletedTransactions.map((t) => t.id))}
        />
      )}

      {activeTab === 'batches' && (
        <BatchesView
          batches={importBatches}
          onDelete={handleDeleteBatch}
        />
      )}

      {activeTab === 'accounts' && (
        <AccountsView
          accounts={accountsWithStats}
          summary={accountsSummary}
          onEdit={(account) => {
            setEditingAccount(account);
            setAccountFormData({
              name: account.name,
              account_type: account.account_type,
              institution: account.institution || '',
              balance: account.balance.toString(),
              currency: account.currency,
            });
            setShowAccountModal(true);
          }}
          onDelete={async (id) => {
            if (!window.confirm('Are you sure you want to delete this account? This will not delete associated transactions.')) return;
            try {
              await accountsAPI.delete(id);
              fetchAccountsWithStats();
            } catch (error) {
              console.error('Failed to delete account:', error);
            }
          }}
          onAdd={() => {
            setEditingAccount(null);
            setAccountFormData({ name: '', account_type: 'checking', institution: '', balance: '', currency: 'CAD' });
            setShowAccountModal(true);
          }}
          onViewHistory={async (account) => {
            setBalanceHistoryAccount(account);
            setShowBalanceHistoryModal(true);
            try {
              const response = await accountsAPI.getBalanceHistory(account.id);
              setBalanceHistory(response.data.history || []);
            } catch (error) {
              console.error('Failed to fetch balance history:', error);
              setBalanceHistory([]);
            }
          }}
        />
      )}

      {/* Pagination (only for main transactions) */}
      {activeTab === 'all' && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', padding: '1rem' }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <span style={{ padding: '0.5rem 1rem' }}>Page {page} of {totalPages}</span>
          <button className="btn btn-secondary btn-sm" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {importStep === 'upload' && 'Import Transactions'}
                {importStep === 'mapping' && `Configure Import${importFiles.length > 1 ? ` (${currentFileIndex + 1}/${importFiles.length})` : ''}`}
                {importStep === 'result' && 'Import Results'}
              </h3>
              <button className="modal-close" onClick={closeImportModal}>&times;</button>
            </div>

            {/* Step: Upload */}
            {importStep === 'upload' && (
              <div
                className="file-upload"
                style={{ minHeight: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📂</div>
                <div style={{ fontWeight: 500, marginBottom: '0.5rem' }}>
                  Drop files here or click to upload
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Supports CSV, OFX, QFX files
                </div>
                <input
                  id="file-input"
                  type="file"
                  accept=".csv,.ofx,.qfx"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            {/* Step: Mapping */}
            {importStep === 'mapping' && importPreview && (
              <div style={{ padding: '1rem' }}>
                {/* File info */}
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>{importPreview.filename_info?.emoji || '📄'}</span>
                    <div>
                      <div style={{ fontWeight: 600 }}>{importFiles[currentFileIndex]?.name}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {importPreview.total_rows} rows | {importPreview.file_type.toUpperCase()} format
                      </div>
                    </div>
                  </div>
                  {importPreview.filename_info?.suggested_name && (
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      Suggested account: <strong>{importPreview.filename_info.suggested_name}</strong>
                    </div>
                  )}
                </div>

                {/* Account Selection */}
                <div className="form-group">
                  <label className="form-label">Link to Account</label>
                  <select
                    className="form-input"
                    value={selectedAccount || ''}
                    onChange={(e) => setSelectedAccount(e.target.value ? parseInt(e.target.value) : null)}
                  >
                    <option value="">Create new or select existing...</option>
                    {accounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>{acc.name}</option>
                    ))}
                  </select>
                </div>

                {/* Column Mapping */}
                {importPreview.file_type === 'csv' && (
                  <>
                    <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Column Mapping</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {requiredFields.map(({ key, label, required }) => (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{ width: '140px', fontWeight: 500 }}>
                            {label}{required && <span style={{ color: 'var(--danger)' }}>*</span>}
                          </div>
                          <select
                            className="form-input"
                            style={{ flex: 1 }}
                            value={columnMapping[key] || ''}
                            onChange={(e) => setColumnMapping({ ...columnMapping, [key]: e.target.value })}
                          >
                            <option value="">Select column...</option>
                            {importPreview.headers.map((h) => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>

                    {/* Date Format */}
                    <div className="form-group" style={{ marginTop: '1rem' }}>
                      <label className="form-label">Date Format</label>
                      <select
                        className="form-input"
                        value={dateFormat}
                        onChange={(e) => setDateFormat(e.target.value)}
                      >
                        <option value="%Y-%m-%d">YYYY-MM-DD (2025-01-15)</option>
                        <option value="%m/%d/%Y">MM/DD/YYYY (01/15/2025)</option>
                        <option value="%d/%m/%Y">DD/MM/YYYY (15/01/2025)</option>
                        <option value="%Y/%m/%d">YYYY/MM/DD (2025/01/15)</option>
                        <option value="%b %d, %Y">Mon DD, YYYY (Jan 15, 2025)</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Options */}
                <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={useAI}
                      onChange={(e) => setUseAI(e.target.checked)}
                    />
                    <span>Use AI to categorize</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={flagDuplicates}
                      onChange={(e) => setFlagDuplicates(e.target.checked)}
                    />
                    <span>Flag duplicates (don't skip)</span>
                  </label>
                </div>

                {/* Preview Table */}
                <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Preview</h4>
                <div style={{ overflow: 'auto', maxHeight: '200px', border: '1px solid var(--border)', borderRadius: '0.5rem' }}>
                  <table className="table" style={{ fontSize: '0.875rem', margin: 0 }}>
                    <thead>
                      <tr>
                        {importPreview.headers.map((h) => (
                          <th key={h} style={{ whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.preview_rows.slice(0, 5).map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j} style={{ whiteSpace: 'nowrap' }}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Confidence indicator */}
                {importPreview.confidence < 0.8 && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.5rem', color: 'var(--warning)' }}>
                    Low confidence in column detection. Please verify the mapping above.
                  </div>
                )}
              </div>
            )}

            {/* Step: Result */}
            {importStep === 'result' && importResult && (
              <div style={{ padding: '1rem' }}>
                {importResult.map((result: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      padding: '1rem',
                      marginBottom: '0.75rem',
                      background: result.error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                      borderRadius: '0.5rem',
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{result.filename}</div>
                    {result.error ? (
                      <div style={{ color: 'var(--danger)' }}>{result.error}</div>
                    ) : (
                      <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.875rem' }}>
                        <span style={{ color: 'var(--success)' }}>Imported: {result.imported}</span>
                        {result.duplicates > 0 && (
                          <span style={{ color: 'var(--warning)' }}>Duplicates flagged: {result.duplicates}</span>
                        )}
                        {result.categorized > 0 && (
                          <span style={{ color: 'var(--primary)' }}>AI categorized: {result.categorized}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeImportModal}>
                {importStep === 'result' ? 'Done' : 'Cancel'}
              </button>
              {importStep === 'mapping' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={importing || !columnMapping.date_column || !columnMapping.description_column || !hasAmountMapping}
                >
                  {importing ? 'Importing...' : `Import ${importFiles.length} File${importFiles.length > 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Transactions</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>&times;</button>
            </div>

            <div style={{ padding: '1rem' }}>
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Deleted transactions can be restored within 30 days.
              </p>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                {(['selected', 'date_range', 'account'] as const).map((mode) => (
                  <button
                    key={mode}
                    className={`btn btn-sm ${deleteMode === mode ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setDeleteMode(mode)}
                  >
                    {mode === 'selected' && `Selected (${selectedTransactions.size})`}
                    {mode === 'date_range' && 'Date Range'}
                    {mode === 'account' && 'By Account'}
                  </button>
                ))}
              </div>

              {deleteMode === 'date_range' && (
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={deleteDateStart}
                      onChange={(e) => setDeleteDateStart(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={deleteDateEnd}
                      onChange={(e) => setDeleteDateEnd(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {deleteMode === 'account' && (
                <div className="form-group">
                  <label className="form-label">Account</label>
                  <select
                    className="form-input"
                    value={deleteAccountId || ''}
                    onChange={(e) => setDeleteAccountId(e.target.value ? parseInt(e.target.value) : null)}
                  >
                    <option value="">Select account...</option>
                    {accounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>{acc.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button
                className="btn btn-danger"
                onClick={handleSoftDelete}
                disabled={
                  (deleteMode === 'selected' && selectedTransactions.size === 0) ||
                  (deleteMode === 'date_range' && (!deleteDateStart || !deleteDateEnd)) ||
                  (deleteMode === 'account' && !deleteAccountId)
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Category Modal */}
      {showBulkCategoryModal && (
        <div className="modal-overlay" onClick={() => { setShowBulkCategoryModal(false); setShowNewCategoryForm(false); }}>
          <div className="modal" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Set Category</h3>
              <button className="modal-close" onClick={() => { setShowBulkCategoryModal(false); setShowNewCategoryForm(false); }}>&times;</button>
            </div>

            <div style={{ padding: '1rem' }}>
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Set category for {selectedTransactions.size} selected transaction{selectedTransactions.size > 1 ? 's' : ''}.
              </p>

              {!showNewCategoryForm ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select
                      className="form-input"
                      value={bulkCategoryId || ''}
                      onChange={(e) => setBulkCategoryId(e.target.value ? parseInt(e.target.value) : null)}
                      autoFocus
                    >
                      <option value="">Select category...</option>
                      {sortedCategoriesForDropdown.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                          {cat.budget_priority ? ` (${cat.budget_priority})` : cat.name === 'Ignore' ? ' (Excluded)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowNewCategoryForm(true)}
                    style={{ marginTop: '0.5rem' }}
                  >
                    + Add New Category
                  </button>
                </>
              ) : (
                <div style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem' }}>
                  <div className="form-group">
                    <label className="form-label">Category Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="e.g., Subscriptions, Fees"
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Budget Priority</label>
                    <select
                      className="form-input"
                      value={newCategoryPriority}
                      onChange={(e) => setNewCategoryPriority(e.target.value)}
                    >
                      <option value="">None (Excluded from budget)</option>
                      <option value="Critical">Critical</option>
                      <option value="Important">Important</option>
                      <option value="Luxury">Luxury</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setShowNewCategoryForm(false); setNewCategoryName(''); setNewCategoryPriority(''); }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleQuickAddCategory}
                      disabled={!newCategoryName.trim()}
                    >
                      Create & Select
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowBulkCategoryModal(false); setShowNewCategoryForm(false); }}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleBulkCategoryApply}
                disabled={!bulkCategoryId || showNewCategoryForm}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Add/Edit Modal */}
      {showAccountModal && (
        <div className="modal-overlay" onClick={() => setShowAccountModal(false)}>
          <div className="modal" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingAccount ? 'Edit Account' : 'Add Account'}</h3>
              <button className="modal-close" onClick={() => setShowAccountModal(false)}>&times;</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const data = {
                  ...accountFormData,
                  balance: parseFloat(accountFormData.balance) || 0,
                };
                if (editingAccount) {
                  await accountsAPI.update(editingAccount.id, data);
                } else {
                  await accountsAPI.create(data);
                }
                setShowAccountModal(false);
                setEditingAccount(null);
                setAccountFormData({ name: '', account_type: 'checking', institution: '', balance: '', currency: 'CAD' });
                fetchAccountsWithStats();
              } catch (error) {
                console.error('Failed to save account:', error);
              }
            }}>
              <div className="form-group">
                <label className="form-label">Account Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={accountFormData.name}
                  onChange={(e) => setAccountFormData({ ...accountFormData, name: e.target.value })}
                  placeholder="e.g., TD Chequing, CIBC Visa"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Account Type</label>
                <select
                  className="form-input"
                  value={accountFormData.account_type}
                  onChange={(e) => setAccountFormData({ ...accountFormData, account_type: e.target.value })}
                >
                  <option value="checking">Chequing</option>
                  <option value="savings">Savings</option>
                  <option value="credit">Credit Card</option>
                  <option value="investment">Investment</option>
                  <option value="loan">Loan</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Institution (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={accountFormData.institution}
                  onChange={(e) => setAccountFormData({ ...accountFormData, institution: e.target.value })}
                  placeholder="e.g., TD Bank, CIBC"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Current Balance</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={accountFormData.balance}
                  onChange={(e) => setAccountFormData({ ...accountFormData, balance: e.target.value })}
                  placeholder="0.00"
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  For credit cards, enter the amount owing as a positive number.
                </small>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAccountModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingAccount ? 'Save Changes' : 'Add Account'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Balance History Modal */}
      {showBalanceHistoryModal && balanceHistoryAccount && (
        <div className="modal-overlay" onClick={() => { setShowBalanceHistoryModal(false); setShowAddSnapshotForm(false); }}>
          <div className="modal" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Balance History - {balanceHistoryAccount.name}</h3>
              <button className="modal-close" onClick={() => { setShowBalanceHistoryModal(false); setShowAddSnapshotForm(false); }}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Current Balance */}
              <div className="card" style={{ padding: '1rem', marginBottom: '1rem', background: 'var(--bg-secondary)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Current Balance</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 600, color: balanceHistoryAccount.balance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {formatCurrency(balanceHistoryAccount.balance)}
                    </div>
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setShowAddSnapshotForm(!showAddSnapshotForm);
                      setSnapshotFormData({ date: new Date().toISOString().split('T')[0], balance: '', notes: '' });
                    }}
                  >
                    {showAddSnapshotForm ? 'Cancel' : '+ Add Snapshot'}
                  </button>
                </div>
              </div>

              {/* Add Snapshot Form */}
              {showAddSnapshotForm && (
                <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      await accountsAPI.createBalanceSnapshot(balanceHistoryAccount.id, {
                        date: snapshotFormData.date,
                        balance: parseFloat(snapshotFormData.balance) || 0,
                        notes: snapshotFormData.notes,
                        update_current_balance: true
                      });
                      setShowAddSnapshotForm(false);
                      // Refresh balance history
                      const response = await accountsAPI.getBalanceHistory(balanceHistoryAccount.id);
                      setBalanceHistory(response.data.history || []);
                      fetchAccountsWithStats();
                    } catch (error) {
                      console.error('Failed to create snapshot:', error);
                    }
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group">
                        <label className="form-label">Date</label>
                        <input
                          type="date"
                          className="form-input"
                          value={snapshotFormData.date}
                          onChange={(e) => setSnapshotFormData({ ...snapshotFormData, date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Balance</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-input"
                          value={snapshotFormData.balance}
                          onChange={(e) => setSnapshotFormData({ ...snapshotFormData, balance: e.target.value })}
                          placeholder="0.00"
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Notes (optional)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={snapshotFormData.notes}
                        onChange={(e) => setSnapshotFormData({ ...snapshotFormData, notes: e.target.value })}
                        placeholder="e.g., Statement balance, Month-end balance"
                      />
                    </div>
                    <button type="submit" className="btn btn-primary btn-sm">Save Snapshot</button>
                  </form>
                </div>
              )}

              {/* Balance History Chart (simple visual) */}
              {balanceHistory.length > 1 && (
                <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
                  <h4 style={{ marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Balance Trend</h4>
                  <div style={{ height: '120px', display: 'flex', alignItems: 'flex-end', gap: '2px', padding: '0 0.5rem' }}>
                    {balanceHistory.slice(0, 30).reverse().map((item, idx) => {
                      const maxBalance = Math.max(...balanceHistory.slice(0, 30).map(h => Math.abs(h.balance)));
                      const height = maxBalance > 0 ? (Math.abs(item.balance) / maxBalance) * 100 : 0;
                      return (
                        <div
                          key={idx}
                          style={{
                            flex: 1,
                            minWidth: '8px',
                            height: `${Math.max(height, 5)}%`,
                            background: item.balance >= 0 ? 'var(--success)' : 'var(--danger)',
                            borderRadius: '2px 2px 0 0',
                            opacity: item.source === 'manual' ? 1 : 0.6
                          }}
                          title={`${item.date}: ${formatCurrency(item.balance)}`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Balance History Table */}
              <div className="card">
                <div className="table-container" style={{ maxHeight: '300px', overflow: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th style={{ textAlign: 'right' }}>Balance</th>
                        <th>Source</th>
                        <th>Notes</th>
                        <th style={{ width: '60px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {balanceHistory.length === 0 ? (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                            No balance history available. Import transactions with balance data or add manual snapshots.
                          </td>
                        </tr>
                      ) : (
                        balanceHistory.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.date}</td>
                            <td style={{ textAlign: 'right', fontWeight: 500, color: item.balance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                              {formatCurrency(item.balance)}
                            </td>
                            <td>
                              <span style={{
                                padding: '0.125rem 0.5rem',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                background: item.source === 'manual' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                                color: item.source === 'manual' ? 'var(--primary)' : 'var(--text-secondary)'
                              }}>
                                {item.source === 'manual' ? 'Manual' : 'Import'}
                              </span>
                            </td>
                            <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{item.notes || '-'}</td>
                            <td>
                              {item.id && (
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={async () => {
                                    if (!window.confirm('Delete this snapshot?')) return;
                                    try {
                                      await accountsAPI.deleteBalanceSnapshot(item.id!);
                                      const response = await accountsAPI.getBalanceHistory(balanceHistoryAccount.id);
                                      setBalanceHistory(response.data.history || []);
                                    } catch (error) {
                                      console.error('Failed to delete snapshot:', error);
                                    }
                                  }}
                                >
                                  &times;
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-components

interface TransactionTableProps {
  transactions: Transaction[];
  categories: Category[];
  selectedTransactions: Set<number>;
  onToggleSelection: (id: number) => void;
  onToggleSelectAll: () => void;
  onCategoryChange: (txnId: number, catId: number) => void;
  onDelete: (id: number) => void;
  sortColumn: 'date' | 'description' | 'account' | 'category' | 'amount';
  sortDirection: 'asc' | 'desc';
  onSort: (column: 'date' | 'description' | 'account' | 'category' | 'amount') => void;
}

const TransactionTable = React.memo<TransactionTableProps>(({
  transactions,
  categories,
  selectedTransactions,
  onToggleSelection,
  onToggleSelectAll,
  onCategoryChange,
  onDelete,
  sortColumn,
  sortDirection,
  onSort,
}) => {
  if (transactions.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: 'var(--text-secondary)' }}>
          No transactions yet. Import a file to get started.
        </p>
      </div>
    );
  }

  const SortableHeader: React.FC<{
    column: 'date' | 'description' | 'account' | 'category' | 'amount';
    label: string;
    align?: 'left' | 'right';
  }> = ({ column, label, align = 'left' }) => (
    <th
      onClick={() => onSort(column)}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {label}
        {sortColumn === column && (
          <span style={{ fontSize: '0.75rem' }}>
            {sortDirection === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <div className="card">
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectedTransactions.size === transactions.length && transactions.length > 0}
                  onChange={onToggleSelectAll}
                />
              </th>
              <SortableHeader column="date" label="Date" />
              <SortableHeader column="description" label="Description" />
              <SortableHeader column="account" label="Account" />
              <SortableHeader column="category" label="Category" />
              <SortableHeader column="amount" label="Amount" align="right" />
              <th style={{ width: '100px' }}>Status</th>
              <th style={{ width: '80px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((txn) => (
              <tr key={txn.id} style={{ opacity: txn.is_reviewed ? 1 : 0.8 }}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedTransactions.has(txn.id)}
                    onChange={() => onToggleSelection(txn.id)}
                  />
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {new Date(txn.date).toLocaleDateString('en-CA')}
                </td>
                <td>
                  <div>{txn.description}</div>
                  {txn.tags && txn.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.25rem' }}>
                      {txn.tags.map((tag) => (
                        <span
                          key={tag.id}
                          style={{
                            fontSize: '0.75rem',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '9999px',
                            backgroundColor: tag.color + '33',
                            color: tag.color,
                          }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  {txn.account?.name || '-'}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <select
                      className="form-input"
                      style={{ padding: '0.5rem', fontSize: '0.875rem', flex: 1 }}
                      value={txn.category?.id || ''}
                      onChange={(e) => onCategoryChange(txn.id, parseInt(e.target.value))}
                    >
                      <option value="">Uncategorized</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                    {txn.category?.budget_priority && (
                      <span
                        className={`badge badge-${txn.category.budget_priority.toLowerCase()}`}
                        style={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                      >
                        {txn.category.budget_priority}
                      </span>
                    )}
                    {txn.category?.name === 'Ignore' && !txn.category?.budget_priority && (
                      <span style={{ fontSize: '0.7rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', background: 'rgba(100, 116, 139, 0.2)', color: '#64748b', whiteSpace: 'nowrap' }}>
                        Ignored
                      </span>
                    )}
                  </div>
                </td>
                <td style={{
                  textAlign: 'right',
                  fontWeight: 500,
                  color: txn.transaction_type === 'credit' ? 'var(--success)' : 'var(--danger)',
                }}>
                  {txn.transaction_type === 'debit' ? '-' : '+'}{formatCurrency(txn.amount)}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                    {txn.is_duplicate && (
                      <span style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', background: 'rgba(245, 158, 11, 0.2)', color: 'var(--warning)' }}>
                        Duplicate
                      </span>
                    )}
                    {txn.needs_review && (
                      <span style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', background: 'rgba(59, 130, 246, 0.2)', color: 'var(--primary)' }}>
                        Review
                      </span>
                    )}
                    {txn.ai_categorized && !txn.is_reviewed && (
                      <span style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', background: 'rgba(168, 85, 247, 0.2)', color: '#a855f7' }}>
                        AI
                      </span>
                    )}
                    {txn.is_reviewed && !txn.needs_review && !txn.is_duplicate && (
                      <span style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', background: 'rgba(16, 185, 129, 0.2)', color: 'var(--success)' }}>
                        Reviewed
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => onDelete(txn.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

interface DuplicatesViewProps {
  duplicates: Transaction[];
  onDelete: (id: number) => void;
  onDeleteAll: () => void;
  onMarkNotDuplicate: (id: number) => void;
}

const DuplicatesView = React.memo<DuplicatesViewProps>(({ duplicates, onDelete, onDeleteAll, onMarkNotDuplicate }) => {
  if (duplicates.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: 'var(--text-secondary)' }}>No duplicate transactions found.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'var(--text-secondary)' }}>
          {duplicates.length} duplicates found. Review and delete as needed, or mark as "Not a Duplicate" if flagged incorrectly.
        </span>
        <button className="btn btn-danger btn-sm" onClick={onDeleteAll}>
          Delete All Duplicates
        </button>
      </div>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Account</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th style={{ width: '220px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {duplicates.map((txn) => (
              <tr key={txn.id}>
                <td>{new Date(txn.date).toLocaleDateString('en-CA')}</td>
                <td>{txn.description}</td>
                <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{txn.account?.name || '-'}</td>
                <td style={{ textAlign: 'right', color: txn.transaction_type === 'credit' ? 'var(--success)' : 'var(--danger)' }}>
                  {txn.transaction_type === 'debit' ? '-' : '+'}{formatCurrency(txn.amount)}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => onMarkNotDuplicate(txn.id)}
                      title="This is NOT a duplicate - keep it"
                    >
                      Not a Dupe
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => onDelete(txn.id)}
                      title="Delete this duplicate"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

interface DeletedViewProps {
  transactions: Transaction[];
  onRestore: (ids: number[]) => void;
  onRestoreAll: () => void;
}

const DeletedView = React.memo<DeletedViewProps>(({ transactions, onRestore, onRestoreAll }) => {
  if (transactions.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: 'var(--text-secondary)' }}>No deleted transactions to restore.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'var(--text-secondary)' }}>
          Transactions are permanently deleted after 30 days.
        </span>
        <button className="btn btn-primary btn-sm" onClick={onRestoreAll}>
          Restore All
        </button>
      </div>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Account</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th>Deleted</th>
              <th style={{ width: '100px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((txn) => (
              <tr key={txn.id} style={{ opacity: 0.7 }}>
                <td>{new Date(txn.date).toLocaleDateString('en-CA')}</td>
                <td>{txn.description}</td>
                <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{txn.account?.name || '-'}</td>
                <td style={{ textAlign: 'right', color: txn.transaction_type === 'credit' ? 'var(--success)' : 'var(--danger)' }}>
                  {txn.transaction_type === 'debit' ? '-' : '+'}{formatCurrency(txn.amount)}
                </td>
                <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  {txn.deleted_at ? new Date(txn.deleted_at).toLocaleDateString('en-CA') : '-'}
                  {txn.deleted_by && ` (${txn.deleted_by})`}
                </td>
                <td>
                  <button className="btn btn-primary btn-sm" onClick={() => onRestore([txn.id])}>
                    Restore
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

interface BatchesViewProps {
  batches: ImportBatch[];
  onDelete: (id: number) => void;
}

const BatchesView = React.memo<BatchesViewProps>(({ batches, onDelete }) => {
  if (batches.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: 'var(--text-secondary)' }}>No import batches yet.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Filename</th>
              <th>Type</th>
              <th>Imported</th>
              <th>Duplicates</th>
              <th>Date</th>
              <th style={{ width: '100px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => (
              <tr key={batch.id}>
                <td>
                  <div>{batch.filename}</div>
                  {batch.account && (
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      {batch.account.name}
                    </div>
                  )}
                </td>
                <td>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    background: 'var(--bg-secondary)',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                  }}>
                    {batch.file_type}
                  </span>
                </td>
                <td>{batch.imported_count}</td>
                <td>
                  {batch.duplicate_count > 0 ? (
                    <span style={{ color: 'var(--warning)' }}>{batch.duplicate_count}</span>
                  ) : (
                    <span style={{ color: 'var(--text-secondary)' }}>0</span>
                  )}
                </td>
                <td style={{ fontSize: '0.875rem' }}>
                  {new Date(batch.created_at).toLocaleDateString('en-CA')}
                </td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => onDelete(batch.id)}>
                    Delete All
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// Helper functions for AccountsView
const getAccountTypeColor = (type: string): string => {
  switch (type) {
    case 'checking': return '#3b82f6';
    case 'savings': return '#10b981';
    case 'credit': return '#ef4444';
    case 'investment': return '#8b5cf6';
    case 'loan': return '#f59e0b';
    default: return '#6b7280';
  }
};

const getAccountTypeLabel = (type: string): string => {
  switch (type) {
    case 'checking': return 'Chequing';
    case 'savings': return 'Savings';
    case 'credit': return 'Credit Card';
    case 'investment': return 'Investment';
    case 'loan': return 'Loan';
    default: return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

interface AccountsViewProps {
  accounts: AccountWithStats[];
  summary: AccountsSummary;
  onEdit: (account: AccountWithStats) => void;
  onDelete: (id: number) => void;
  onAdd: () => void;
  onViewHistory: (account: AccountWithStats) => void;
}

const AccountsView = React.memo<AccountsViewProps>(({ accounts, summary, onEdit, onDelete, onAdd, onViewHistory }) => {
  const assetAccounts = accounts.filter(a => !['credit', 'loan'].includes(a.account_type));
  const liabilityAccounts = accounts.filter(a => ['credit', 'loan'].includes(a.account_type));

  return (
    <div>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Total Assets</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--success)' }}>
            {formatCurrency(summary.total_assets)}
          </div>
        </div>
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Total Liabilities</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--danger)' }}>
            {formatCurrency(summary.total_liabilities)}
          </div>
        </div>
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Net Position</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 600, color: summary.net_worth >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            {formatCurrency(summary.net_worth)}
          </div>
        </div>
      </div>

      {/* Add Account Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="btn btn-primary" onClick={onAdd}>+ Add Account</button>
      </div>

      {/* Asset Accounts */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Assets</h3>
          <span style={{ color: 'var(--success)', fontWeight: 600 }}>{formatCurrency(summary.total_assets)}</span>
        </div>
        {assetAccounts.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Institution</th>
                  <th>Transactions</th>
                  <th>Last Activity</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assetAccounts.map((account) => (
                  <tr key={account.id}>
                    <td style={{ fontWeight: 500 }}>{account.name}</td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        backgroundColor: getAccountTypeColor(account.account_type) + '20',
                        color: getAccountTypeColor(account.account_type),
                      }}>
                        {getAccountTypeLabel(account.account_type)}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{account.institution || '-'}</td>
                    <td>{account.transaction_count || 0}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      {account.last_transaction_date || '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--success)' }}>
                      {formatCurrency(account.balance)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => onViewHistory(account)}>History</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => onEdit(account)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => onDelete(account.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ padding: '1rem', color: 'var(--text-secondary)' }}>No asset accounts yet. Import transactions or add an account to get started.</p>
        )}
      </div>

      {/* Liability Accounts */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Liabilities</h3>
          <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{formatCurrency(summary.total_liabilities)}</span>
        </div>
        {liabilityAccounts.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Institution</th>
                  <th>Transactions</th>
                  <th>Last Activity</th>
                  <th style={{ textAlign: 'right' }}>Balance Owing</th>
                  <th style={{ width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {liabilityAccounts.map((account) => (
                  <tr key={account.id}>
                    <td style={{ fontWeight: 500 }}>{account.name}</td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        backgroundColor: getAccountTypeColor(account.account_type) + '20',
                        color: getAccountTypeColor(account.account_type),
                      }}>
                        {getAccountTypeLabel(account.account_type)}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{account.institution || '-'}</td>
                    <td>{account.transaction_count || 0}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      {account.last_transaction_date || '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--danger)' }}>
                      {formatCurrency(Math.abs(account.balance))}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => onViewHistory(account)}>History</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => onEdit(account)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => onDelete(account.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ padding: '1rem', color: 'var(--text-secondary)' }}>No liability accounts (credit cards, loans) yet.</p>
        )}
      </div>
    </div>
  );
});

export default Transactions;
